import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../core/context/AuthContext";
import AppLoadingShell from "../../core/components/AppLoadingShell";
import "./BusinessMode.css";
import BusinessSidebarBrand from "./BusinessSidebarBrand";
import BusinessSidebarNav from "./BusinessSidebarNav";
import BusinessSidebarUserMenu from "./BusinessSidebarUserMenu";
import { WhatsNewProvider, useWhatsNew } from "./WhatsNewContext";
import BusinessSidebarRailToggleIcon from "./BusinessSidebarRailToggleIcon";
import ActionItemsSidebarNavSync from "./ActionItemsSidebarNavSync";
import { AiCreditsUsageProvider } from "./shared/AiCreditsUsageContext";
import {
  ActionItemsSidebarProvider,
  useActionItemsSidebar,
} from "./ActionItemsSidebarContext";
import {
  isBusinessActionItemsHomePath,
  isBusinessConnectorSetupPath,
  readBusinessOnboardingConnectSetup,
  resolveConnectSourcesOnboardingGate,
  shouldHideBusinessSidebar,
} from "./actionItemHelpers";
import { usePerformerOAuthCallback } from "./usePerformerOAuthCallback";

/**
 * True when running inside the macOS Electron shell (hiddenInset titlebar).
 *
 * @example
 * isElectronDarwin() // true in Electron on Mac; false in browser
 */
const isElectronDarwin = () =>
  Boolean(
    typeof window !== "undefined" &&
      window.starmeshDesktop?.isElectron &&
      window.starmeshDesktop?.platform === "darwin"
  );

/**
 * Business shell: brand sidebar, account footer, Granola-style panel toggle.
 * Collapsed = fully hidden (no icon rail / profile). On macOS Electron the
 * toggle sits next to the traffic lights in the inset titlebar.
 *
 * @example
 * <BusinessModeLayout />
 */
const BusinessModeLayoutContent = () => {
  usePerformerOAuthCallback();
  const location = useLocation();
  const navigate = useNavigate();
  const { items, workflows, itemsLoading, lastFetchedAt } = useActionItemsSidebar();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [electronDarwin] = useState(() => isElectronDarwin());
  const { user } = useAuth();
  const whatsNew = useWhatsNew();
  const userName = String(user?.name || "").trim();
  const userEmail = String(user?.email || "").trim();
  const sidebarCollapsed = isSidebarCollapsed;
  const isActionItemsHome = isBusinessActionItemsHomePath(location.pathname);
  const hideSidebar = shouldHideBusinessSidebar(location.pathname);
  const isConnectorSetupPath = isBusinessConnectorSetupPath(location.pathname);
  const oauthReturnParams = new URLSearchParams(location.search);
  const isOauthConnectorReturn =
    Boolean(oauthReturnParams.get("code")) ||
    oauthReturnParams.get("oauth") === "success";
  const isOnboardingConnectorSetup =
    isConnectorSetupPath &&
    (location.state?.from === "onboarding" ||
      (isOauthConnectorReturn && readBusinessOnboardingConnectSetup()));
  const hasFetchedActionItems = lastFetchedAt != null;
  const { showGate, shouldRedirect } = resolveConnectSourcesOnboardingGate({
    hasFetchedActionItems,
    itemsLoading,
    itemsCount: items.length,
    workflowsCount: workflows.length,
    search: location.search,
  });
  const showSidebarToggle = !hideSidebar;

  useEffect(() => {
    if (!isActionItemsHome || !shouldRedirect) return;
    navigate("/onboarding", { replace: true });
  }, [isActionItemsHome, navigate, shouldRedirect]);

  useEffect(() => {
    if (!electronDarwin) return undefined;
    document.documentElement.classList.add("starmesh-electron-darwin");
    return () => {
      document.documentElement.classList.remove("starmesh-electron-darwin");
    };
  }, [electronDarwin]);

  if (isActionItemsHome && showGate) {
    return (
      <AppLoadingShell
        text={shouldRedirect ? "Opening connectors…" : "Loading…"}
      />
    );
  }

  // Keep refreshing even when the sidebar is hidden so post-setup lands on action
  // items with an up-to-date workflows list (avoids stale empty-account redirects).
  const sidebarNavSync = <ActionItemsSidebarNavSync />;

  const panelToggle = showSidebarToggle ? (
    <button
      type="button"
      className={`business-sidebar-panel-toggle${
        electronDarwin ? " business-sidebar-panel-toggle--traffic-adjacent" : ""
      }`}
      onClick={() => setIsSidebarCollapsed((value) => !value)}
      aria-label={
        sidebarCollapsed
          ? `Expand business sidebar${whatsNew?.hasUnseen ? " (something new)" : ""}`
          : "Collapse business sidebar"
      }
      aria-expanded={!sidebarCollapsed}
      title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
    >
      <BusinessSidebarRailToggleIcon collapsed={sidebarCollapsed} />
      {sidebarCollapsed && whatsNew?.hasUnseen ? (
        <span className="whats-new-dot whats-new-dot--toggle" data-testid="toggle-whats-new-dot" />
      ) : null}
    </button>
  ) : null;

  return (
    <div
      className={`main main--business${hideSidebar ? " main--business-no-sidebar" : ""}${
        showSidebarToggle && sidebarCollapsed
          ? " main--business-sidebar-hidden"
          : ""
      }${electronDarwin ? " main--business-electron-darwin" : ""}`}
    >
      {sidebarNavSync}
      {electronDarwin ? (
        <div className="business-window-drag-region" aria-hidden="true" />
      ) : null}
      {!hideSidebar && !sidebarCollapsed ? (
        <aside className="business-sidebar-shell" aria-label="Business navigation">
          <BusinessSidebarBrand collapsed={false} />
          <BusinessSidebarNav items={items} />
          <div className="business-sidebar-footer">
            <BusinessSidebarUserMenu
              userName={userName}
              userEmail={userEmail}
              collapsed={false}
            />
          </div>
        </aside>
      ) : null}
      <div
        className={`content${
          showSidebarToggle ? " content--with-sidebar-toggle" : ""
        }`}
      >
        <div
          className={`page-container business-page-container${
            showSidebarToggle ? " business-page-container--with-panel-toggle" : ""
          }${
            showSidebarToggle && sidebarCollapsed
              ? " business-page-container--sidebar-collapsed"
              : ""
          }${
            isOnboardingConnectorSetup
              ? " business-page-container--onboarding-setup"
              : ""
          }`}
        >
          <Outlet />
        </div>
      </div>
      {/*
        Keep the toggle LAST in the DOM: Electron builds the macOS drag region by
        walking the DOM in tree order (a later no-drag control subtracts from any
        earlier drag surface, regardless of z-index), so a control that sits over
        a drag surface must come after it to stay clickable.
      */}
      {panelToggle}
    </div>
  );
};

const BusinessModeLayout = () => (
  <AiCreditsUsageProvider>
    <ActionItemsSidebarProvider>
      <WhatsNewProvider>
        <BusinessModeLayoutContent />
      </WhatsNewProvider>
    </ActionItemsSidebarProvider>
  </AiCreditsUsageProvider>
);

export default BusinessModeLayout;
